'use client';

import { useState, useRef, useEffect } from 'react';
import { cn } from '../_lib/utils';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper';
import { Swiper, type SwiperProps, type SwiperRef, SwiperSlide } from 'swiper/react';
import { CardSectionHeader, type CardSectionHeaderProps } from '../CardSectionHeader';
import { Container } from '../Container';
import { ProductCard, type ProductCardProps } from '../ProductCard';

export type ProductCardCarouselProps = CardSectionHeaderProps & {
	className?: string;
	items: ProductCardProps[];
};

export function ProductCardCarousel(props: ProductCardCarouselProps) {
	const [, setActiveIndex] = useState(0);
	const [, updateSwiper] = useState<SwiperRef['swiper']>();

	const SwiperConfig: SwiperProps = {
		centeredSlides: false,
		grabCursor: true,
		keyboard: {
			enabled: true,
		},
		slidesPerGroup: 1,
		slidesPerView: 1,
		spaceBetween: 32,
		speed: 480,
		breakpoints: {
			768: {
				slidesPerGroup: 3,
				slidesPerView: 3,
			},
		},
		onSlideChange(swiper) {
			setActiveIndex(swiper.activeIndex);
		},
		onSwiper(swiper) {
			updateSwiper(swiper);
		},
		pagination: {
			el: '.custom-pagination',
			clickable: true,
			renderBullet: (index, className) => {
				return `<span class='${className}'><span></span></span>`;
			},
		},
	};

	const divRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (divRef.current) {
			const isTouchScreen = 'ontouchstart' in window;
			if (isTouchScreen) {
				divRef.current.setAttribute('data-lenis-prevent', 'true');
			} else {
				divRef.current.removeAttribute('data-lenis-prevent');
			}
		}
	}, []);

	return (
		<div className={cn('overflow-hidden', props.className)} data-component='ProductCardCarousel'>
			<Container className={`flex flex-col gap-responsive-2xl`}>
				{props.title && <CardSectionHeader overline={props.overline} title={props.title} button={props.button} />}
				<div
					ref={divRef}
					className={cn(
						`relative md:self-center min-w-0 w-full [&>div]:overflow-visible h-full z-0`,
						props.items.length <= 3 ? '[&_.custom-pagination]:hidden' : '',
					)}
				>
					<Swiper
						modules={[Navigation, A11y, Keyboard, Pagination]}
						{...SwiperConfig}
						className='max-md:[&_.swiper-wrapper]:translate-x-0! max-md:[&_.swiper-wrapper]:flex! max-md:[&_.swiper-wrapper]:flex-col! max-md:[&_.swiper-wrapper]:gap-responsive-sm! max-md:[&_.swiper-wrapper]:w-[calc(100vw-var(--container-padding)-var(--container-padding))]! md:[&>div]:flex md:[&>div]:flex-row md:[&_.swiper-slide]:h-auto'
					>
						{props.items?.map((item, index) => {
							return (
								<SwiperSlide className='md:h-auto!' key={`product-card-carousel-${index}`}>
									<ProductCard {...item} className={cn('md:h-full', item.className)} />
								</SwiperSlide>
							);
						})}
						<div
							className={`custom-pagination flex flex-row justify-center mt-responsive-xl max-md:hidden [&_.swiper-pagination-bullet]:px-1 [&_.swiper-pagination-bullet]:py-4 [&_.swiper-pagination-bullet_span]:block [&_.swiper-pagination-bullet_span]:transition-all [&_.swiper-pagination-bullet_span]:duration-normal [&_.swiper-pagination-bullet_span]:ease-smooth [&_.swiper-pagination-bullet_span]:h-1 [&_.swiper-pagination-bullet_span]:rounded-full [&_.swiper-pagination-bullet_span]:bg-background-brand [&_.swiper-pagination-bullet_span]:opacity-30 [&_.swiper-pagination-bullet_span]:w-[1.5rem] [&_.swiper-pagination-bullet]:cursor-pointer [&_.swiper-pagination-bullet:hover_span]:opacity-60 [&_.swiper-pagination-bullet:hover_span]:w-[2rem] [&_.swiper-pagination-bullet-active_span]:opacity-100! [&_.swiper-pagination-bullet-active_span]:w-[3rem]!`}
						></div>
					</Swiper>
				</div>
			</Container>
		</div>
	);
}
